import {theme as defaultTheme} from '@miro-site/design-tokens'

const colors = {
  secondaryText: '#686786',
  strokeColor: '#F0F0F0',
  pink: '#FBE6E5',
  blue: '#4E6CFF',
  biege: '#FAFAFA',
}

const animation = {
  defaultFunction: 'cubic-bezier(0, 0, 0.16, 0.96)',
}

const header = {
  height: 90,
  background: defaultTheme.colors.primaryWhite,

  animation,

  grid: {
    columns: [3, 3, 6, 12, 18],
    gutter: [20, 20, 20, 20, 20],
  },

  container: {
    maxWidth: 1460,
    horizontalPadding: [20, 20, 20, 30],
  },

  navigation: {
    padding: {
      right: [56, 56, null],
    },
  },

  logo: {
    color: 'primaryIndigo',
    width: 91,
    height: 32,
    offset: {
      top: -5,
    },
  },

  topNavigation: {
    leftOffset: [24, 24, 24, 24, 32],
    gutter: [8, 8, 8, 8, 12],

    color: defaultTheme.colors.primaryIndigo,
    hoverColor: defaultTheme.colors.primaryBlueHover,

    animation: {
      linkIndicator: `transform .35s ${animation.defaultFunction}`,
      chevron: `transform 250ms ${animation.defaultFunction}`,
    },

    fontSize: [null, 16, 16, 16, 18],
    lineHeight: 24,

    contactSales: {
      icon: {
        width: 24,
        height: 24,
      },
    },
  },

  ctaNavigation: {
    gutter: 16,

    button: {
      type: 'primary',
      size: {
        borderRadius: 24,
        fontSize: [18, 16],
        lineHeight: '100%',
      },
      spacing: {
        paddingTop: [14, 16],
        paddingRight: 13,
        paddingBottom: [14, 16],
        paddingLeft: 21,
      },
    },

    mobileButton: {
      type: 'primary',
      size: {
        borderRadius: 24,
        fontSize: 16,
        lineHeight: 24,
      },
      spacing: {
        paddingTop: 11,
        paddingRight: 13,
        paddingBottom: 13,
        paddingLeft: 21,
      },
    },
  },

  secondaryNavigation: {
    animation: {
      translateOffset: 80,
      navigationDelay: '200ms',
      highlightedDelay: '250ms',
      container: `transform 0.35s ${animation.defaultFunction}, opacity 0.35s ${animation.defaultFunction}`,
      columns: `visibility .4s, opacity .2s ${animation.defaultFunction}, transform .4s ${animation.defaultFunction}`,
    },
    background: defaultTheme.colors.primaryWhite,

    boxShadow: '0px 60px 100px rgba(0, 0, 0, 0.05)',

    stroke: `1px solid ${colors.strokeColor}`,

    verticalPadding: 40,

    headline: {
      as: 'h4',
      variant: 'h4',
      verticalMargin: 5,
    },

    viewAllLink: {
      variant: 'regular',
      color: 'default',
      withArrow: true,
      fontSize: 18,
      lineHeight: 24,
      marginTop: 21,
    },

    item: {
      title: {
        styles: {
          transition: 'color .2s linear',
          color: defaultTheme.colors.primaryIndigo,
          fontSize: 18,
          lineHeight: 24,
        },
        hoverStyles: {
          color: defaultTheme.colors.primaryBlueHover,
        },
      },

      description: {
        styles: {
          transition: 'color .2s linear',
          color: colors.secondaryText,
          fontSize: 14,
          lineHeight: 20,
        },
        hoverStyles: {
          color: defaultTheme.colors.primaryIndigo,
        },
      },

      maxWidth: 160,

      margins: {
        before: 20,
        after: 5,
        afterTitle: 6,
      },

      hover: {
        animation: {
          show: 'opacity 0.2s linear, transform 0.2s ease-out',
          hide: 'opacity 80ms linear, transform 80ms ease-in',
        },
        radius: 8,
        background: colors.biege,
        offset: {
          top: 8,
          right: 6,
          bottom: 9,
          left: 6,
        },
      },
    },
  },

  bottomLinks: {
    background: defaultTheme.colors.primaryWhite,
    height: 60,
    paddingTop: 17,
    paddingBottom: 19,
    gutter: 24,

    link: {
      variant: 'regular',
      color: 'default',
      size: 'default',
      border: 'none',
      withArrow: true,
    },
  },

  customerSpotlight: {
    imageWidth: 226,
    logoWidth: 112,
    verticalPadding: 25,
    horizontalPadding: 40,
    quote: {
      variant: 'p2',
      color: colors.secondaryText,
    },
    viewAllLink: {
      variant: 'regular',
      color: 'default',
      size: 'default',
      withArrow: true,
    },

    headline: {
      as: 'h4',
      variant: 'h4',
    },

    margins: {
      afterHeadline: 15,
      afterImage: 23,
      afterLogo: 18,
      afterQuote: 28,
    },
  },

  highlighted: {
    verticalPadding: 27,
    horizontalPadding: 32,
    radius: 10,
    background: colors.pink,

    imageWidth: '35%',
    textWidth: '60%',
    imageWidthV1: '100%',
    imageWidthV2: '20%',
    textWidthV2: '80%',

    margins: {
      afterHeadline: 24,
      afterItem: 25,
      afterImage: '5%',
      afterTitle: 4,
    },

    headline: {
      as: 'h4',
      variant: 'h4',
    },

    itemTitle: {
      variant: 'p1',
    },

    itemDescription: {
      variant: 'p2',
      color: colors.secondaryText,
    },

    viewAllLink: {
      variant: 'regular',
      color: 'default',
      size: 'default',
      withArrow: true,
    },
  },

  burger: {
    color: 'primaryIndigo',
    hoverColor: 'outspoken',
    activeColor: 'white',
    size: 32,
    margin: {
      left: 23,
    },
    offset: {
      right: 20,
    },
    line: {
      width: 22,
      height: '2px',
      radius: 50,
      offset: {
        default: '8px',
        active: 15,
      },
    },
  },

  colors,
}

const accordion = {
  background: 'primaryIndigo',
  gutter: 20,
  itemStyles: {
    borderTop: '1px solid #9B99AF',
  },
  innerList: {
    margin: {
      top: 28,
    },
  },
  innerItem: {
    margin: {
      top: 28,
    },
  },
  categoryWrapper: {
    padding: [20, 24],
  },
  category: {
    fontSize: 18,
    fontWeight: 700,
    lineHeight: 20,
    color: 'white',
  },
  description: {
    fontSize: 14,
    lineHeight: 20,
    color: 'white',
    opacity: '0.8',
    margin: {
      top: 6,
    },
  },
  subcategoryWrapper: {
    padding: {
      left: [20, 34],
      top: 34,
      bottom: 32,
    },
    margin: {
      top: -32,
    },
  },
  subcategory: {
    fontSize: 18,
    fontWeight: 700,
    lineHeight: 24,
    color: 'white',
    margin: {
      top: 30,
    },
  },
  additionalLink: {
    margin: {
      top: 30,
    },
  },
  innerLink: {
    fontSize: 18,
    lineHeight: 24,
  },
}

const theme = {
  ...defaultTheme,
  viewportsArray: ['v2Mobile', 'v2Tablet', 'v2Desktop', 'v2LargeDesktop', 'v2XLargeDesktop'],
  header,
  accordion,
}

export type Theme = typeof theme

export default theme
